import { useMemo, useState } from "react";
import type { AvatarConfig, AvatarGender } from "@guandan/avatar-core";
import { ActionButton, GlassPanel } from "@guandan/ui";
import {
  avatarPortraitForConfig,
  avatarPortraitsForGender,
} from "../game/avatar-portraits";
import { PortraitAvatar } from "./PortraitAvatar";
import { userAvatarApi } from "../lib/user-avatar";

export function AvatarCreator({
  initial,
  initialNickname,
  nicknameLocked = false,
  onSave,
  onCancel,
}: {
  initial?: AvatarConfig;
  initialNickname?: string;
  nicknameLocked?: boolean;
  forceStatic?: boolean;
  onSave: (nickname: string, avatar: AvatarConfig) => void | Promise<void>;
  onCancel?: () => void;
}) {
  const fallback = firstPortrait("woman");
  const initialPortrait = avatarPortraitForConfig(initial ?? fallback.config);
  const [nickname, setNickname] = useState(initialNickname ?? "");
  const [gender, setGender] = useState<AvatarGender>(initialPortrait.gender);
  const [selectedId, setSelectedId] = useState(initialPortrait.id);
  const [customPortraitId, setCustomPortraitId] = useState(
    initial?.customPortraitId ?? null,
  );
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const portraits = useMemo(() => avatarPortraitsForGender(gender), [gender]);
  const selected =
    portraits.find((item) => item.id === selectedId) ?? firstPortrait(gender);
  const selectedConfig: AvatarConfig = customPortraitId
    ? { ...selected.config, customPortraitId }
    : selected.config;

  function switchGender(nextGender: AvatarGender) {
    const first = firstPortrait(nextGender);
    setGender(nextGender);
    setSelectedId(first.id);
    setCustomPortraitId(null);
  }

  async function uploadAvatar(file: File) {
    if (uploading || busy) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("头像图片不能超过 5 MB");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const uploaded = await userAvatarApi.upload(file);
      setCustomPortraitId(uploaded.id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "头像上传失败，请重试");
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    const cleanNickname = nickname.normalize("NFKC").trim();
    if (
      nicknameLength(cleanNickname) < 2 ||
      nicknameLength(cleanNickname) > 12 ||
      busy
    )
      return;
    setBusy(true);
    setError("");
    try {
      await onSave(cleanNickname, selectedConfig);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "头像资料保存失败，请重试",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="creator-screen avatar-picker-screen">
      <header className="creator-header">
        <div>
          <h1>{initial ? "选择头像" : "创建牌手资料"}</h1>
          <p>选择喜欢的现代头像；牌桌、大厅与战绩会使用同一头像。</p>
        </div>
        {onCancel ? (
          <ActionButton tone="ghost" size="sm" onClick={onCancel}>
            返回
          </ActionButton>
        ) : null}
      </header>
      <div className="avatar-picker-layout">
        <GlassPanel className="avatar-picker-preview">
          <PortraitAvatar
            config={selectedConfig}
            label={
              customPortraitId
                ? "自定义头像"
                : `${selected.ageLabel}${selected.label}头像`
            }
          />
          <strong>{customPortraitId ? "我的头像" : selected.label}</strong>
          <span>{customPortraitId ? "已保存到 R2" : selected.ageLabel}</span>
        </GlassPanel>
        <GlassPanel className="avatar-picker-controls">
          <div className="gender-switch" role="group" aria-label="头像性别">
            <button
              type="button"
              aria-pressed={gender === "woman"}
              onClick={() => switchGender("woman")}
            >
              女性头像
            </button>
            <button
              type="button"
              aria-pressed={gender === "man"}
              onClick={() => switchGender("man")}
            >
              男性头像
            </button>
          </div>
          <label className="field">
            <span>昵称</span>
            <input
              disabled={nicknameLocked}
              maxLength={12}
              value={nickname}
              onChange={(event) => setNickname(event.target.value)}
              placeholder="2—12 个字符"
              autoComplete="nickname"
            />
            {nicknameLocked ? (
              <small>昵称已注册并绑定你的战绩，不能更改。</small>
            ) : null}
          </label>
          <label className="avatar-upload-control">
            <span>{uploading ? "正在上传…" : "上传自己的头像"}</span>
            <small>JPG、PNG 或 WebP，最大 5 MB；将安全保存到 R2。</small>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={uploading || busy}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = "";
                if (file) void uploadAvatar(file);
              }}
            />
          </label>
          <div
            className="portrait-choice-grid"
            role="radiogroup"
            aria-label="可选头像"
          >
            {portraits.map((portrait) => (
              <button
                type="button"
                role="radio"
                aria-checked={selected.id === portrait.id}
                key={portrait.id}
                onClick={() => {
                  setSelectedId(portrait.id);
                  setCustomPortraitId(null);
                }}
              >
                <PortraitAvatar
                  config={portrait.config}
                  label={`${portrait.ageLabel}${portrait.label}`}
                />
                <span>
                  <b>{portrait.label}</b>
                  <small>{portrait.ageLabel}</small>
                </span>
              </button>
            ))}
          </div>
          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          <ActionButton
            tone="orange"
            size="lg"
            wide
            disabled={
              busy ||
              uploading ||
              nicknameLength(nickname.normalize("NFKC").trim()) < 2 ||
              nicknameLength(nickname.normalize("NFKC").trim()) > 12
            }
            onClick={() => void save()}
          >
            {busy
              ? "正在保存…"
              : initial
                ? nicknameLocked
                  ? "保存头像"
                  : "保存昵称与头像"
                : "完成创建"}
          </ActionButton>
        </GlassPanel>
      </div>
    </main>
  );
}

function nicknameLength(value: string): number {
  return Array.from(
    new Intl.Segmenter("zh-CN", { granularity: "grapheme" }).segment(value),
  ).length;
}

function firstPortrait(gender: AvatarGender) {
  const value = avatarPortraitsForGender(gender)[0];
  if (!value) throw new Error(`缺少${gender}头像目录`);
  return value;
}
